import React, { useContext } from 'react';
import { Store } from '@website/Store';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { SubmitButton } from '@website/components/forms/fields/SubmitButton';
import OfflineAlert from '@website/components/utilities/OfflineAlert';
import { useMutation } from '@apollo/client';
import {
  SendSupportMessageDocument,
  SupportMessageTags,
} from '@website/types/graphqlOperations';

/**
 * Contact Form
 */
export default function ContactForm({
  defaultMessage = '',
  defaultSubject = '',
  messagePlaceholder,
  hideTitle = false,
  tags = [],
  subjectLabel = 'Subject',
}: {
  defaultMessage?: string;
  defaultSubject?: string;
  subjectLabel?: string;
  messagePlaceholder?: string;
  hideTitle?: boolean;
  tags?: SupportMessageTags[];
}) {
  const { state } = useContext(Store);
  const [sendSupportMessage, { data, loading, error, reset }] = useMutation(
    SendSupportMessageDocument,
  );

  const defaultValues = {
    name: state?.user?.first_name,
    email: state?.user?.email || '',
    subject: defaultSubject,
    message: defaultMessage,
  };

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    reset: resetForm,
  } = useForm({
    defaultValues,
  });

  if (loading) {
    return (
      <div className="mt-3">
        {!hideTitle && <h2 className="h4">Contact Skyhook</h2>}

        <Spinner animation="border" />
      </div>
    );
  }

  if (data) {
    return (
      <div className="mt-3">
        {!hideTitle && <h2 className="h4">Contact Skyhook</h2>}

        <Alert variant="success">
          <Alert.Heading>
            {navigator?.onLine === false
              ? "We'll try to send the message later"
              : 'Message sent'}
          </Alert.Heading>
          <p>
            {navigator?.onLine === false && (
              <>
                We'll try to send the message when you next have an internet
                connection, however it may fail to send.
              </>
            )}{' '}
            We typically reply within 1 working day.
          </p>
          <Button
            variant="success"
            onClick={() => {
              reset();
              resetForm();
            }}
          >
            Send another message
          </Button>
        </Alert>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-3">
        {!hideTitle && <h2 className="h4">Contact Skyhook</h2>}

        <Alert variant="warning">
          <Alert.Heading>Sending failed</Alert.Heading>
          <p>Sorry - sending this message failed. Please try again.</p>
          <Button
            variant="success"
            onClick={() => {
              reset();
            }}
          >
            Try again
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <OfflineAlert message="We recommend waiting until you have a 3G/WiFi connection before sending us a message." />

      {!hideTitle && <h2 className="h4">Contact Skyhook</h2>}

      <Form
        onSubmit={handleSubmit(async (values: typeof defaultValues) => {
          await sendSupportMessage({
            variables: {
              input: {
                ...values,
                tags: [...tags, SupportMessageTags.HELP_PAGE],
              },
            },
            optimisticResponse: { sendSupportMessage: { success: true } },
          });
        })}
      >
        <Form.Group controlId="name">
          <Form.Label>Name *</Form.Label>
          <Form.Control {...register('name')} autoComplete="name" required />
        </Form.Group>

        <Form.Group controlId="contactemail">
          <Form.Label>Email *</Form.Label>
          <Form.Control
            {...register('email')}
            autoComplete="email"
            required
            type="email"
          />
        </Form.Group>

        <Form.Group controlId="subject">
          <Form.Label>{subjectLabel} *</Form.Label>
          <Form.Control {...register('subject')} required />
        </Form.Group>

        <Form.Group controlId="message">
          <Form.Label>Message *</Form.Label>
          <Form.Control
            {...register('message')}
            required
            as="textarea"
            rows={8}
            placeholder={messagePlaceholder}
          />
        </Form.Group>

        <SubmitButton
          block
          isSubmitting={isSubmitting}
          className="mt-2"
          text="Send"
        />
      </Form>
    </div>
  );
}
